import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import { activateNewestServiceWorker } from '@/lib/pwa/activateNewestServiceWorker'
import { revalidateAppShell } from '@/lib/pwa/revalidateAppShell'
import type {
  BeforeInstallPromptEvent,
  PwaServiceWorkerUpdateFn,
} from '@/types/beforeInstallPromptEvent'

const INSTALL_SNOOZE_KEY = 'evergpt-pwa-install-snoozed-until'
const APPLYING_UPDATE_SESSION_KEY = 'evergpt-pwa-applying-update'
const DEFAULT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000

function isStandaloneDisplay(): boolean {
  try {
    if (localStorage.getItem('evergpt-pwa-debug-standalone') === '1') {
      return true
    }
  } catch {
    /* ignore */
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  )
}

function getSnoozeUntil(): number {
  const raw = localStorage.getItem(INSTALL_SNOOZE_KEY)
  return raw ? Number(raw) : 0
}

function readApplyingUpdateSession(): boolean {
  try {
    return sessionStorage.getItem(APPLYING_UPDATE_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function writeApplyingUpdateSession(active: boolean) {
  try {
    if (active) {
      sessionStorage.setItem(APPLYING_UPDATE_SESSION_KEY, '1')
    } else {
      sessionStorage.removeItem(APPLYING_UPDATE_SESSION_KEY)
    }
  } catch {
    /* private mode / quota */
  }
}

export const usePwaStore = defineStore('pwa', () => {
  const isApplyingUpdate = ref(readApplyingUpdateSession())
  const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(null)
  const swNeedsRefresh = ref(false)
  const updateSw = ref<PwaServiceWorkerUpdateFn | null>(null)

  /**
   * The "a new version is ready" prompt.
   *
   * No longer restricted to the installed/standalone display. A superseded build is
   * just as stale in a browser tab — that is where most users are — and that gate is
   * why they were never offered the reload after a deploy. `isApplyingUpdate` still
   * suppresses it while a reload we already started is in flight.
   */
  const showUpdateNotification = computed(
    () => !isApplyingUpdate.value && swNeedsRefresh.value,
  )

  const showInstallNotification = computed(() => {
    if (isApplyingUpdate.value) return false
    if (!deferredInstallPrompt.value) return false
    if (isStandaloneDisplay()) return false
    return Date.now() >= getSnoozeUntil()
  })

  const setDeferredInstallPrompt = (e: BeforeInstallPromptEvent | null) => {
    deferredInstallPrompt.value = e
  }

  const setUpdateSwFn = (fn: PwaServiceWorkerUpdateFn | null) => {
    updateSw.value = fn
  }

  const onUpdateAvailable = () => {
    // Clear a stale applying flag from an interrupted reload so the prompt can show.
    if (isApplyingUpdate.value) {
      isApplyingUpdate.value = false
      writeApplyingUpdateSession(false)
    }
    swNeedsRefresh.value = true
  }

  const finishApplyingUpdate = () => {
    if (!isApplyingUpdate.value) return
    isApplyingUpdate.value = false
    swNeedsRefresh.value = false
    writeApplyingUpdateSession(false)
  }

  const onServiceWorkerRegistered = () => {
    if (isApplyingUpdate.value && !swNeedsRefresh.value) {
      finishApplyingUpdate()
    }
  }

  const onServiceWorkerControllerChange = () => {
    if (isApplyingUpdate.value) {
      finishApplyingUpdate()
    }
  }

  const dismissUpdatePrompt = () => {
    swNeedsRefresh.value = false
  }

  const snoozeInstallOffer = () => {
    localStorage.setItem(
      INSTALL_SNOOZE_KEY,
      String(Date.now() + DEFAULT_SNOOZE_MS),
    )
    deferredInstallPrompt.value = null
  }

  const promptInstall = async () => {
    const e = deferredInstallPrompt.value
    if (!e) return
    await e.prompt()
    await e.userChoice
    deferredInstallPrompt.value = null
  }

  const applyUpdate = async () => {
    isApplyingUpdate.value = true
    writeApplyingUpdateSession(true)
    try {
      const fn = updateSw.value
      if (fn) {
        // `virtual:pwa-register` activates the new worker and reloads by itself.
        await fn(true)
        return
      }

      /**
       * No registered updater (the `onNeedRefresh` path is intentionally unwired
       * under autoUpdate, so this is the live path). A bare reload is not enough
       * when a service worker is in control: the navigation goes through its fetch
       * handler and comes back from the SAME precached shell the user is trying to
       * get off, so nothing changes and the prompt returns. Hand control to the
       * newest worker first — a no-op for users who have none.
       */
      await activateNewestServiceWorker()
    } catch {
      /* fall through to the reload below */
    }
    /**
     * Handing over the service worker is only half of it — `index.html` ships with no
     * `Cache-Control`, so a plain reload can be answered from the browser's HTTP cache
     * and bring the tab back on the same stale build, which is exactly what makes this
     * prompt reappear immediately after the user acts on it. Refresh that cache entry
     * first; see revalidateAppShell.ts.
     */
    await revalidateAppShell()
    window.location.reload()
  }

  return {
    showUpdateNotification,
    showInstallNotification,
    setDeferredInstallPrompt,
    setUpdateSwFn,
    onUpdateAvailable,
    // Also cleared by the version watch: with no service worker there is nothing to
    // report a controller change, so an applied update would otherwise leave the flag
    // set for the whole session and mute both prompts.
    finishApplyingUpdate,
    onServiceWorkerRegistered,
    onServiceWorkerControllerChange,
    dismissUpdatePrompt,
    snoozeInstallOffer,
    promptInstall,
    applyUpdate,
  }
})
